import { useState } from "react";
import { useRules } from "../api/queries";
import RulesList from "../components/RulesList";
import RuleEditor from "../components/RuleEditor";
import PresetEditor from "../components/PresetEditor";
import FallbackRuleCard from "../components/FallbackRuleCard";
import Modal from "../components/Modal";
import type { Rule } from "../api/types";

type Tab = "rules" | "presets";
type EditTarget = Rule | "new" | null;

export default function RulesPage() {
  const rules = useRules();
  const [tab, setTab] = useState<Tab>("rules");
  const [editing, setEditing] = useState<EditTarget>(null);

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <TabBtn active={tab === "rules"} onClick={() => setTab("rules")}>
          Rules
        </TabBtn>
        <TabBtn active={tab === "presets"} onClick={() => setTab("presets")}>
          Presets
        </TabBtn>
      </div>

      {tab === "rules" ? (
        <div className="space-y-4">
          <EngineExplainer />

          <div className="flex justify-end">
            <button type="button" onClick={() => setEditing("new")} className="btn-primary">
              + New rule
            </button>
          </div>

          {rules.isLoading ? (
            <p className="text-sm text-slate-400">Loading rules…</p>
          ) : rules.isError || !rules.data ? (
            <p className="text-sm text-rose-400">Failed to load rules.</p>
          ) : (
            <RulesList rules={rules.data.rules} onEdit={(r) => setEditing(r)} />
          )}

          <FallbackRuleCard />

          <Modal
            open={editing !== null}
            onClose={() => setEditing(null)}
            title={editing === "new" ? "Create rule" : editing ? `Edit rule · ${editing.name}` : ""}
            size="lg"
          >
            {editing !== null ? (
              <RuleEditor
                rule={editing === "new" ? null : editing}
                onDone={() => setEditing(null)}
              />
            ) : null}
          </Modal>
        </div>
      ) : (
        <PresetEditor />
      )}
    </div>
  );
}

function EngineExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200"
      >
        <span>How the rule engine works</span>
        <span className="text-slate-500">{open ? "hide" : "show"}</span>
      </button>

      {open ? (
        <div className="mt-3 space-y-3 leading-relaxed">
          <p>
            Every{" "}
            <span className="font-mono text-slate-200">evaluation_interval_seconds</span> (5s by
            default), the evaluator runs one tick. A tick is gated first by the{" "}
            <em>override</em> and <em>pause</em> switches — if either is active, the LED is not
            touched. Otherwise the engine walks the enabled rules in ascending{" "}
            <span className="font-mono">priority</span> order and, for each rule, iterates the
            candidate events in this order: real events currently effective at{" "}
            <span className="font-mono">now</span> (all-day first, then meetings by start time
            ascending), followed by a single synthetic placeholder event appended at the end.
          </p>

          <p>
            A rule's <em>predicate</em> is a conjunction (AND) of the fields you set — any field
            left at <span className="font-mono">any</span> is ignored. Event-level predicates
            (title regex, calendar name regex, <span className="font-mono">all_day</span>,{" "}
            <span className="font-mono">my_response</span>,{" "}
            <span className="font-mono">visibility</span>,{" "}
            <span className="font-mono">primary calendar</span>,{" "}
            <span className="font-mono">I am organizer</span>, event type, status, transparency)
            are tested against the candidate event. Day-of-week and time-window predicates are
            tested against the evaluator's clock, not the event's. Context predicates
            (<span className="font-mono">no events</span>,{" "}
            <span className="font-mono">no effective events</span>,{" "}
            <span className="font-mono">working hours</span>,{" "}
            <span className="font-mono">not working day</span>) are tested against the tick
            context derived from your Working Hours configuration.
          </p>

          <p>
            The synthetic placeholder exists so that rules with only context predicates can still
            fire when no real event is effective. A rule may match the synthetic only when it has
            at least one context predicate AND no event-level predicate — this prevents accidental
            matches on a placeholder that has no real attributes.
          </p>

          <p>
            The first <span className="font-mono">(rule, event)</span> pair that matches decides
            the tick:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>
              <span className="font-mono">preset</span> — the rule fires with the preset's color /
              luminance / effect. Evaluation terminates.
            </li>
            <li>
              <span className="font-mono">do nothing</span> — the rule fires but commands no LED
              change. Evaluation terminates.
            </li>
            <li>
              <span className="font-mono">skip</span> — the event is dropped from the remaining
              candidate list for this tick; evaluation moves on to the next rule with a reduced
              event set. Use this to filter noise (e.g. declined, cancelled, working-location
              events) before the catch-all rules run.
            </li>
          </ul>

          <p>
            If the entire rule walk exhausts with no terminal hit, the singleton <em>fallback</em>{" "}
            rule decides the outcome (card at the bottom of this page). If the fallback is
            disabled, the LED is left alone.
          </p>

          <p>
            The <strong>Matched Event</strong> is the real calendar event whose match caused the
            evaluator to terminate — shown in the hero on the Status page and highlighted in the
            events list. There is at most one per tick, and two cases produce no Matched Event
            (the rule still fires, but no event is attributed): (a) the match was on the synthetic
            placeholder, or (b) the matching rule is <em>context-only</em> — it has at least one
            context predicate AND no event-level predicate. In case (b) the walked real event is
            just the first candidate not filtered by event-level predicates; it isn't
            semantically the event that caused the rule to fire.
          </p>
        </div>
      ) : null}
    </section>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-4 py-1.5 text-sm ${
        active
          ? "border-brand bg-brand/20 text-brand"
          : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
      }`}
    >
      {children}
    </button>
  );
}
